import { useEffect, useState } from "react";
import { AnimatePresence, motion } from "framer-motion";
import { Link } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { Cookie } from "lucide-react";
import { Button } from "@/components/ui/button";

const STORAGE_KEY = "adam-safety-cookie-consent";
export const COOKIE_SETTINGS_EVENT = "adam-safety:open-cookie-settings";

export function CookieConsent() {
  const { t } = useTranslation("common");
  const [visible, setVisible] = useState(false);

  useEffect(() => {
    try {
      if (!localStorage.getItem(STORAGE_KEY)) setVisible(true);
    } catch {
      setVisible(true);
    }

    const reopen = () => setVisible(true);
    window.addEventListener(COOKIE_SETTINGS_EVENT, reopen);
    return () => window.removeEventListener(COOKIE_SETTINGS_EVENT, reopen);
  }, []);

  const decide = (value: "accepted" | "declined") => {
    try {
      localStorage.setItem(
        STORAGE_KEY,
        JSON.stringify({ value, date: new Date().toISOString() })
      );
    } catch {
      /* storage unavailable - ignore */
    }
    setVisible(false);
  };

  return (
    <AnimatePresence>
      {visible && (
        <motion.div
          role="dialog"
          aria-live="polite"
          aria-label={t("cookies.title")}
          initial={{ y: 120, opacity: 0 }}
          animate={{ y: 0, opacity: 1 }}
          exit={{ y: 120, opacity: 0 }}
          transition={{ duration: 0.4, ease: [0.22, 1, 0.36, 1] }}
          className="fixed inset-x-0 z-[60] px-3 sm:px-4"
          style={{
            bottom: "max(env(safe-area-inset-bottom, 0px) + 88px, 96px)",
          }}
        >
          <div className="container mx-auto max-w-4xl rounded-2xl border border-border bg-card/95 backdrop-blur-md shadow-2xl p-4 sm:p-5">
            <div className="flex flex-col sm:flex-row sm:items-center gap-4">
              <div className="flex items-start gap-3 flex-1">
                <span className="shrink-0 w-10 h-10 rounded-xl bg-accent/10 flex items-center justify-center">
                  <Cookie className="w-5 h-5 text-accent" aria-hidden="true" />
                </span>
                <div className="text-sm text-muted-foreground leading-relaxed">
                  <p className="font-semibold text-foreground mb-1">
                    {t("cookies.title")}
                  </p>
                  <p>
                    {t("cookies.description")}{" "}
                    <Link
                      to="/privacy"
                      className="text-accent underline underline-offset-2 hover:opacity-80"
                    >
                      {t("cookies.policyLink")}
                    </Link>
                  </p>
                </div>
              </div>
              <div className="flex gap-2 sm:shrink-0">
                <Button
                  variant="outline"
                  className="flex-1 sm:flex-none min-h-11"
                  onClick={() => decide("declined")}
                >
                  {t("cookies.decline")}
                </Button>
                <Button
                  className="flex-1 sm:flex-none min-h-11 bg-accent text-accent-foreground hover:bg-accent/90"
                  onClick={() => decide("accepted")}
                >
                  {t("cookies.accept")}
                </Button>
              </div>
            </div>
          </div>
        </motion.div>
      )}
    </AnimatePresence>
  );
}
